/**
 * Inbox frame overlays: Kanäle card (bottom-right, inside) + WhatsApp card
 * hanging off the top-right window edge (~45% outside).
 */
"use client";

import { KeyRound, Mail, Unplug } from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { FloatingCallout } from "../../hero-primitives-app";
import { H } from "../../hero-tokens";
import { useInboxUi } from "./inbox-ui-context";

const WA_GREEN = "#25D366";
/** Badge width — paired with RIGHT_OUTSIDE for ~45% overhang. */
const WA_BADGE_WIDTH = 268;
/** Negative right offset → ~45% of badge hangs outside the white window. */
const WA_RIGHT_OUTSIDE = -Math.round(WA_BADGE_WIDTH * 0.45);
/** Top bar 50 + content padding ~18 → lines up with list header row. */
const WA_TOP = 68;
/** Default FloatingCallout width — right offset keeps left edge flush with WhatsApp. */
const KANALE_WIDTH = 216;
const KANALE_RIGHT = WA_BADGE_WIDTH + WA_RIGHT_OUTSIDE - KANALE_WIDTH;

function WhatsAppIcon(props: { readonly size?: number }): ReactNode {
  const size: number = props.size ?? 14;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.15h-.01c-1.53 0-3.03-.41-4.34-1.19l-.31-.18-3.23.85.86-3.15-.2-.32a8.21 8.21 0 0 1-1.26-4.36c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.48-1.38-1.73-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.16-.25.25-.42.08-.16.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.84-.2-.48-.41-.42-.56-.43h-.48c-.16 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.41 1.01 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.29z" />
    </svg>
  );
}

/** Connected channel row (E-Mail / Portal). */
function ChannelRow(props: {
  readonly icon: ReactNode;
  readonly label: string;
}): ReactNode {
  const t = useTranslations("marketing.moduleInboxChannelsCallout");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "5px 0",
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: 7,
          background: H.accentSoft,
          color: H.accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {props.icon}
      </span>
      <span
        style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: "#0B0F19" }}
      >
        {props.label}
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 10.5,
          fontWeight: 600,
          color: "#3A9E6B",
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#3A9E6B",
          }}
        />
        {t("verbunden")}
      </span>
    </div>
  );
}

/**
 * Big WhatsApp card — frame overlay, top-right, ~45% outside the white window
 * (same pattern as Portal phone `bottom: -44`).
 */
function WhatsAppEdgeCallout(): ReactNode {
  const t = useTranslations("marketing.moduleInboxChannelsCallout");
  return (
    <FloatingCallout
      tabLabel={
        <>
          <WhatsAppIcon size={11} />
          {t("neu")}
        </>
      }
      style={{
        position: "absolute",
        top: WA_TOP,
        right: WA_RIGHT_OUTSIDE,
        zIndex: 40,
        width: WA_BADGE_WIDTH,
        border: "2px solid rgba(37,211,102,0.45)",
        boxShadow:
          "0 36px 72px -22px rgba(11,15,25,0.46), 0 14px 28px -10px rgba(37,211,102,0.35)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span
          style={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 12,
            background: WA_GREEN,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 10px 22px -6px rgba(37,211,102,0.7)",
          }}
        >
          <WhatsAppIcon size={24} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: "#0B0F19",
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            {t("whatsappIntegration")}
          </div>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "#128C4A",
              marginTop: 3,
              lineHeight: 1.25,
            }}
          >
            {t("jetztPosteingang")}
          </div>
        </div>
      </div>
    </FloatingCallout>
  );
}

/**
 * Frame-level inbox overlays. Hidden while a Vorgang detail is open.
 */
export function InboxChannelsCallout(): ReactNode {
  const t = useTranslations("marketing.moduleInboxChannelsCallout");
  const { inVorgangDetail } = useInboxUi();
  if (inVorgangDetail) return null;

  return (
    <>
      <WhatsAppEdgeCallout />
      <FloatingCallout
        tabLabel={
          <>
            <Unplug width={10} height={10} strokeWidth={2} />
            {t("kanaele")}
          </>
        }
        style={{
          position: "absolute",
          right: KANALE_RIGHT,
          bottom: 18,
          zIndex: 35,
          width: KANALE_WIDTH,
        }}
      >
        <div
          style={{
            fontSize: 11.5,
            color: "rgba(11,15,25,0.5)",
            marginBottom: 10,
            lineHeight: 1.35,
          }}
        >
          {t("alleEingaengePosteingang")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <ChannelRow
            icon={<Mail width={14} height={14} strokeWidth={1.9} />}
            label={t("eMail")}
          />
          <ChannelRow
            icon={<KeyRound width={14} height={14} strokeWidth={1.9} />}
            label={t("portal")}
          />
        </div>
      </FloatingCallout>
    </>
  );
}
